import { injectable } from "inversify";
import { makeAutoObservable } from "mobx";
import type { CoachAnimationState, ICoachManager } from "@/lib/types/coach";

/**
 * Coach Store - Live coach animation state for public training
 *
 * Mirrors the dashboard CoachManager: the avatar live session emits
 * `coach_mode_activated` / `coach_mode_deactivated` events, the store turns
 * them into animation states and the Coach component plays the matching
 * Lottie segment.
 */
@injectable()
export class LandingCoachStore implements ICoachManager {
  animationState: CoachAnimationState = "initial";
  isInitialPlayed: boolean = false;
  isEnabled: boolean = true;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setEnabled(value: boolean): void {
    this.isEnabled = value;
  }

  completeInitial(): void {
    if (this.animationState === "initial" && !this.isInitialPlayed) {
      this.isInitialPlayed = true;
      this.animationState = "idle";
    }
  }

  startSpeaking(): void {
    if (!this.isInitialPlayed || this.animationState === "speaking") return;
    this.animationState = "speaking";
  }

  stopSpeaking(): void {
    if (this.animationState === "speaking") {
      this.animationState = "idle";
    }
  }

  startListening(): void {
    if (!this.isInitialPlayed || this.animationState === "listening") return;
    this.animationState = "listening";
  }

  stopListening(): void {
    if (this.animationState === "listening") {
      this.animationState = "idle";
    }
  }

  startThinking(): void {
    if (!this.isInitialPlayed || this.animationState !== "idle") return;
    this.animationState = "thinking";
  }

  stopThinking(): void {
    if (this.animationState === "thinking") {
      this.animationState = "idle";
    }
  }

  reset(): void {
    this.isInitialPlayed = false;
    this.animationState = "initial";
  }
}
